import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Dot = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.secondary};
  pointer-events: none;
  z-index: 9999;
  opacity: 0;
  will-change: transform, opacity;
  transition: opacity 0.3s ease;
`;

const Ring = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 32px;
  height: 32px;
  margin: -16px 0 0 -16px;
  border-radius: 50%;
  border: 1px solid rgba(139, 124, 255, 0.55);
  pointer-events: none;
  z-index: 9999;
  opacity: 0;
  will-change: transform, width, height, opacity;
  transition: width 0.25s ease, height 0.25s ease, margin 0.25s ease, border-color 0.25s ease,
    background 0.25s ease, opacity 0.3s ease;
`;

const Cursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = (matches) => {
      setEnabled(matches);
      document.body.classList.toggle("has-custom-cursor", matches);
    };
    apply(mq.matches);
    const listener = (e) => apply(e.matches);
    mq.addEventListener?.("change", listener);
    return () => mq.removeEventListener?.("change", listener);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const target = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ringPos = { ...target };
    let raf;
    let hasMoved = false;

    const move = (e) => {
      target.x = e.clientX;
      target.y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX - 3}px, ${e.clientY - 3}px)`;
        dotRef.current.style.opacity = "1";
      }
      if (ringRef.current) ringRef.current.style.opacity = "1";
      if (!hasMoved) {
        hasMoved = true;
        ringPos.x = e.clientX;
        ringPos.y = e.clientY;
      }
    };

    const hide = () => {
      if (dotRef.current) dotRef.current.style.opacity = "0";
      if (ringRef.current) ringRef.current.style.opacity = "0";
    };

    const setHover = (isHover) => {
      if (ringRef.current) {
        ringRef.current.style.width = isHover ? "56px" : "32px";
        ringRef.current.style.height = isHover ? "56px" : "32px";
        ringRef.current.style.marginLeft = isHover ? "-28px" : "-16px";
        ringRef.current.style.marginTop = isHover ? "-28px" : "-16px";
        ringRef.current.style.borderColor = isHover ? "rgba(34,211,238,0.75)" : "rgba(139,124,255,0.55)";
        ringRef.current.style.background = isHover ? "rgba(34,211,238,0.08)" : "transparent";
      }
    };

    const over = (e) => {
      if (e.target.closest && e.target.closest("a, button, [data-cursor-hover]")) setHover(true);
    };
    const out = (e) => {
      if (e.target.closest && e.target.closest("a, button, [data-cursor-hover]")) setHover(false);
    };

    const tick = () => {
      ringPos.x += (target.x - ringPos.x) * 0.16;
      ringPos.y += (target.y - ringPos.y) * 0.16;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ringPos.x}px, ${ringPos.y}px)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    window.addEventListener("mouseout", out);
    document.documentElement.addEventListener("mouseleave", hide);
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("mouseout", out);
      document.documentElement.removeEventListener("mouseleave", hide);
      cancelAnimationFrame(raf);
      document.body.classList.remove("has-custom-cursor");
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <Dot ref={dotRef} />
      <Ring ref={ringRef} />
    </>
  );
};

export default Cursor;
